import Image from 'next/future/image';
import {
  CategoryLandingPageKey,
  ProductCategoryWarranty,
} from '@lambda/apis/src/category/types';
import { handleFreeReturn } from '@lambda/commons/helpers';
import settings from '@/settings/index';
import StarsIcon from '@/public/icons/collection/category/stars.svg';
import ReturnIcon from '@/public/icons/collection/category/return.svg';
import WarrantyIcon from '@/public/icons/collection/category/warranty.svg';
import { getEdgeUrl } from '@/lib/getEdgeUrl';

import Section from '@/components/commons/Section';
import { formatWarrantyInfo } from '@/lib/warranty';

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

// USPs Props Type
type USPsT = {
  alt: string;
  icon: any;
  label: string;
};

// USPs Component
const USPs = ({ alt, icon, label }: USPsT) => (
  <div className="flex items-center gap-1.5 text-xxs font-bold md:text-xs">
    <div className="h-3 w-3 shrink-0 xxs:h-4 xxs:w-4">
      <Image
        alt={alt}
        src={icon}
        className="h-full w-full"
        height={20}
        width={20}
      />
    </div>
    <span>{label}</span>
  </div>
);

// Handle Return Label
const generateReturnLabel = () => `${freeReturnDays} Days Return`;

// Customers Label
const customersLabel = '500,000+ Customers';

// Default Component
export default function Banner(props: {
  tagline: string;
  handle: CategoryLandingPageKey;
  categoryWarrantyInfo?: ProductCategoryWarranty | null;
}) {
  const { tagline, categoryWarrantyInfo } = props;

  // Generate Warranty Label
  const generateWarrantyLabel = () => {
    const formattedWarrantyInfo = formatWarrantyInfo(categoryWarrantyInfo);

    if (categoryWarrantyInfo?.eligible)
      return `${formattedWarrantyInfo} Warranty`;

    // TODO deprecate store specific warranty logic
    if (settings.warranty_month === 12) return '1 Year Warranty';

    return `${settings.warranty_month} Month Warranty`;
  };

  // Generate Return Label
  const returnLabel = generateReturnLabel();

  // Retrieve Banner Image
  const bannerImage = getEdgeUrl(
    `images/collections/category/banner/${props.handle}.png`,
  );

  return (
    <Section className="lg:reebelo-container pt-0 lg:pt-8">
      <div className="relative flex h-[180px] items-center overflow-hidden bg-[#92F7FA] lg:rounded-xl">
        {/* Banner Image */}
        <div className="absolute right-[-550px] h-full xxs:right-[-480px] sm:right-[-350px] md:right-[-275px] lg:right-0">
          <Image
            src={bannerImage}
            alt="Baby"
            height={2188}
            width={500}
            className="h-full w-auto rounded-br-xl"
            quality={100}
            loading="eager"
          />
        </div>
        {/* Banner Content  */}
        <div className="relative p-5">
          <h1 className="max-w-[200px] text-lg font-extrabold !leading-tight xxs:text-[18px] xs:max-w-[100%] md:text-xl lg:max-w-[250px] xl:max-w-[100%]">
            {tagline}
          </h1>
          <div className="mt-3 flex flex-col gap-1 xs:flex-row xs:gap-3 lg:flex-col lg:gap-1 xl:flex-row xl:items-center xl:gap-5">
            <USPs
              alt="Warranty"
              icon={WarrantyIcon}
              label={generateWarrantyLabel()}
            />
            <USPs alt="Return" icon={ReturnIcon} label={returnLabel} />
            <USPs alt="Customers" icon={StarsIcon} label={customersLabel} />
          </div>
        </div>
      </div>
    </Section>
  );
}
